/**
 * AI CONTEXT
 * Purpose: reusable keyword/tag selector UI with sort modes and custom-tag creation.
 * Inputs: sorted tags + selected IDs + callbacks from standout hook/panel.
 * Used by: Evaluation Standout panel and Teacher quick-tag panel.
 * Importance: core UX for selecting evidence tags before generation/save.
 * Refactor: extract count-badge rendering and max-selection logic utilities.
 */
import { CloseOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Input, Segmented, Tag, Typography } from "antd"
import { sampleSize } from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"

import Flex from "#blocks/Flex"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { TagPool } from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"

import { visibleKeywords } from "./keywordPreview"
import { SortMode, TagWithStats } from "./useStandoutMoments"

const { Text } = Typography

const MAX_SELECTED = 10

interface KeywordPickerProps {
  pool: TagPool
  sortedTags: TagWithStats[]
  selectedTagIds: Set<Uuid>
  sortMode: SortMode
  minRequired?: number
  previewLimit?: number
  onSortModeChange: (mode: SortMode) => void
  onToggleTag: (tagId: Uuid) => void
  onRemoveTag: (tagId: Uuid) => void
  onCreateCustomTag: (text: string, pool: TagPool) => Promise<Uuid | null>
  tagsById: Record<Uuid, { text: string }>
}

const SORT_OPTIONS = [
  { label: "Frequency", value: "frequency" },
  { label: "Recent", value: "recent" },
  { label: "Oldest", value: "oldest" },
  { label: "A-Z", value: "az" },
]

const CountBadges = ({ tag, pool }: { tag: TagWithStats; pool: TagPool }) => {
  const theme = useThemeContext()

  if (pool === "secret") {
    if (!tag.secretCount) return null
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: theme.eric.blue,
          marginLeft: 4,
        }}
      >
        {tag.secretCount}
      </span>
    )
  }

  const hasUp = tag.thumbsUpCount > 0
  const hasDown = tag.needsWorkCount > 0
  if (!hasUp && !hasDown) return null

  return (
    <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 600 }}>
      {hasUp && (
        <span style={{ color: theme.eric.green }}>{tag.thumbsUpCount}</span>
      )}
      {hasUp && hasDown && <span style={{ opacity: 0.4 }}>/</span>}
      {hasDown && (
        <span style={{ color: theme.eric.red }}>{tag.needsWorkCount}</span>
      )}
    </span>
  )
}

export default function KeywordPicker({
  pool,
  sortedTags,
  selectedTagIds,
  sortMode,
  minRequired = 1,
  previewLimit,
  onSortModeChange,
  onToggleTag,
  onRemoveTag,
  onCreateCustomTag,
  tagsById,
}: KeywordPickerProps) {
  const theme = useThemeContext()
  const [customInput, setCustomInput] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [showAllKeywords, setShowAllKeywords] = useState(false)
  const [previewIds, setPreviewIds] = useState<readonly string[]>([])

  const previewIsStale =
    previewLimit != null &&
    sortedTags.length > previewLimit &&
    (previewIds.length !== previewLimit ||
      previewIds.some((id) => !sortedTags.some((tag) => tag.id === id)))
  let resolvedPreviewIds = previewIds
  if (previewIsStale) {
    resolvedPreviewIds = sampleSize([...sortedTags], previewLimit).map(
      (tag) => tag.id
    )
    setPreviewIds(resolvedPreviewIds)
  }

  useEffect(() => {
    setShowAllKeywords(false)
  }, [pool, sortMode])

  const cloudTags = useMemo(
    () =>
      visibleKeywords(
        sortedTags,
        previewLimit,
        showAllKeywords,
        resolvedPreviewIds
      ),
    [previewLimit, resolvedPreviewIds, showAllKeywords, sortedTags]
  )
  const hiddenKeywordCount = sortedTags.length - cloudTags.length

  const selectedCount = selectedTagIds.size
  const isAtMax = selectedCount >= MAX_SELECTED
  const isMinMet = selectedCount >= minRequired

  const handleCustomTagSubmit = useCallback(async () => {
    const trimmed = customInput.trim()
    if (!trimmed || isCreating) return

    setIsCreating(true)
    try {
      const tagId = await onCreateCustomTag(trimmed, pool)
      if (tagId && !selectedTagIds.has(tagId) && !isAtMax) {
        onToggleTag(tagId)
      }
      setCustomInput("")
    } finally {
      setIsCreating(false)
    }
  }, [
    customInput,
    isCreating,
    onCreateCustomTag,
    pool,
    selectedTagIds,
    isAtMax,
    onToggleTag,
  ])

  const selectedTags = useMemo(() => {
    return Array.from(selectedTagIds)
      .map((id) => ({ id, text: tagsById[id]?.text ?? id }))
      .filter((t) => t.text)
  }, [selectedTagIds, tagsById])

  return (
    <Flex col gap={12} style={{ padding: "8px 0" }}>
      {/* Sort Controls */}
      <Flex col gap={4} style={{ maxWidth: 350 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Sort by:
        </Text>
        <Segmented
          options={SORT_OPTIONS}
          value={sortMode}
          onChange={(val) => onSortModeChange(val as SortMode)}
          size="small"
          block
        />
      </Flex>

      {/* Tag Cloud */}
      <Flex
        style={{
          flexWrap: "wrap",
          gap: 6,
          maxHeight: 200,
          overflowY: "auto",
          padding: "8px 0",
        }}
      >
        {cloudTags.map((tag) => {
          const isSelected = selectedTagIds.has(tag.id)
          return (
            <Tag
              key={tag.id}
              style={{
                cursor: isAtMax && !isSelected ? "not-allowed" : "pointer",
                opacity: isAtMax && !isSelected ? 0.5 : 1,
                borderColor: isSelected ? theme.eric.blue : undefined,
                backgroundColor: isSelected
                  ? theme.eric.blue + "18"
                  : undefined,
                fontWeight: isSelected ? 600 : 400,
                display: "inline-flex",
                alignItems: "center",
              }}
              onClick={() => {
                if (isAtMax && !isSelected) return
                onToggleTag(tag.id)
              }}
            >
              {tag.text}
              <CountBadges tag={tag} pool={pool} />
            </Tag>
          )
        })}
        {sortedTags.length === 0 && (
          <Text type="secondary" style={{ fontSize: 12, padding: 8 }}>
            No tags available. Add a custom tag below.
          </Text>
        )}
      </Flex>
      {previewLimit != null && sortedTags.length > previewLimit ? (
        <Button
          type="link"
          size="small"
          onClick={() => setShowAllKeywords((open) => !open)}
          data-testid="keyword-show-more"
          style={{ alignSelf: "flex-start", padding: 0, height: "auto" }}
        >
          {showAllKeywords ? "Show less" : `Show more (${hiddenKeywordCount})`}
        </Button>
      ) : null}

      {/* Custom Tag Input */}
      <Input
        placeholder="Add custom keyword..."
        prefix={<PlusOutlined />}
        value={customInput}
        onChange={(e) => setCustomInput(e.target.value)}
        onPressEnter={handleCustomTagSubmit}
        disabled={isAtMax}
        size="small"
      />

      {/* Selected Keywords */}
      <Flex col gap={4}>
        <Text
          strong
          style={{
            fontSize: 12,
            color: isMinMet ? theme.eric.green : theme.eric.orange,
          }}
        >
          Selected: {selectedCount}/{MAX_SELECTED}
          {!isMinMet && ` (need at least ${minRequired})`}
        </Text>
        <Flex style={{ flexWrap: "wrap", gap: 4, minHeight: 28 }}>
          {selectedTags.map((tag) => (
            <Tag
              key={tag.id}
              closable
              closeIcon={<CloseOutlined />}
              onClose={(e) => {
                e.preventDefault()
                onRemoveTag(tag.id)
              }}
              color="blue"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              {tag.text}
            </Tag>
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}
